import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { InboxService } from '../../../../../../../../services/inbox/inbox.service';
import {
  MembershipPendingStaffApprovalInboxItem,
  StaffApprovalAction,
  UnlinkedMembershipCustomerInboxItem
} from '../../../../../../../../models/inbox';
import { CustomerMembershipStatus } from '../../../../../../../../models/membership-enums';
import { dateStringFromISO8601 } from '../../../../../../../../utils';
import { AccountService } from '../../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../../services/customer/customer.service';
import { Customer, CustomerFilters } from '../../../../../../../../models/customer';
import { Subject } from 'rxjs';
import { ListResponse } from '../../../../../../../../models/list-response';
import { debounceTime } from 'rxjs/operators';
import { CustomerMembership } from '../../../../../../../../models/membership';
import { MembershipService } from '../../../../../../../../services/membership/membership.service';
import { SimpleMessageModalComponent } from '../../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { OrganizationConfigService } from '../../../../../../../../services/organization-config/organization-config.service';

@Component({
  selector: 'app-review-membership-pending-staff-approval-modal',
  templateUrl: './review-membership-pending-staff-approval-modal.component.html',
  styleUrls: ['./review-membership-pending-staff-approval-modal.component.css']
})
export class ReviewMembershipPendingStaffApprovalModalComponent implements OnInit {

  @Input() customerMembershipId: number;
  @Input() showHideOption: boolean;
  @Input() savedCallback: () => undefined;

  public selectedAction: StaffApprovalAction = null;
  public emailText = '';

  public customerMembership: CustomerMembership;

  public loadingSubmit = false;
  public loadingCustomerMembership = true;

  public customerMembershipNotFound = false;
  public errorMessage = '';

  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public dateStringFromISO8601 = dateStringFromISO8601;
  public StaffApprovalAction = StaffApprovalAction;
  public readonly CustomerMembershipStatus = CustomerMembershipStatus;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    private customerService: CustomerService,
    private membershipService: MembershipService,
    private inboxService: InboxService,
    private orgConfig: OrganizationConfigService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    this.loadCustomerMembership();
  }

  public getCustomersParsed(): { name: string, id?: number }[] {
    if (!this.customerMembership) { return []; }

    const customerNames: { name: string, id?: number }[] = [];

    for (const customer of this.customerMembership.all_customers) {
      customerNames.push({
        name: customer.first_name + ' ' + customer.last_name,
        id: customer.id
      });
    }
    for (const unlinkedCustomer of this.customerMembership.unlinked_customers) {
      customerNames.push({
        name: unlinkedCustomer.first_name + ' ' + unlinkedCustomer.last_name + ' (Pending)'
      });
    }

    return customerNames;
  }

  /**
   * Whether a valid input has been selected. Used to determine whether the
   * save button is clickable.
   */
  public validInputSelected(): boolean {
    return (
      [
        StaffApprovalAction.approve,
        StaffApprovalAction.deny,
        StaffApprovalAction.hide
      ].indexOf(this.selectedAction) >= 0
    );
  }

  public handleSave() {
    this.loadingSubmit = true;

    this.errorMessage = '';

    if (!this.validInputSelected()) {
      this.errorMessage = 'Please choose a valid option.';
      this.loadingSubmit = false;
      return;
    }

    let savePromise: Promise<void>;

    if (this.selectedAction === StaffApprovalAction.approve) {
      savePromise = this.inboxService
        .approveMembershipPendingStaffApproval(this.customerMembershipId)
        .then(() => {
          this.inboxService.loadInboxCounts(true).then();
          this.savedCallback();
          this.activeModal.close();
        });
    } else if (this.selectedAction === StaffApprovalAction.deny) {
      savePromise = this.inboxService
        .cancelMembershipPendingStaffApproval(
          this.customerMembershipId,
          this.emailText
        )
        .then(result => {
          this.inboxService.loadInboxCounts(true).then();
          this.savedCallback();
          this.activeModal.close();

          if (result.has_pos_payment) {
            const messageModalRef = this.modalService.open(SimpleMessageModalComponent);
            messageModalRef.componentInstance.headerText = 'Membership Not Refunded';
            messageModalRef.componentInstance.messageText = 'This membership was purchased via your point-of-sale system. The membership has been cancelled successfully, but in order to refund it you must do so in the point-of-sale system';
            messageModalRef.componentInstance.messageAlertClass = 'alert-warning';
            return;
          }
        });
    } else if (this.selectedAction === StaffApprovalAction.hide) {
      savePromise = this.inboxService
        .hideMembershipPendingStaffApproval(this.customerMembershipId)
        .then(() => {
          this.inboxService.loadInboxCounts(true).then();
          this.savedCallback();
          this.activeModal.close();
        });
    } else {
      console.error('Unexpected pending membership action: ' + this.selectedAction);
      return;
    }

    savePromise
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to manage this membership.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  private loadCustomerMembership() {
    this.loadingCustomerMembership = true;

    this.membershipService
      .getCustomerMembership(this.customerMembershipId)
      .then(customerMembership => {
        this.customerMembership = customerMembership;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to manage this membership.';
        } else if (reason.status === 404) {
          this.customerMembershipNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomerMembership = false; });
  }

}
